"use client";

import { AnimatePresence, motion } from "motion/react";
import type { Ref } from "react";

import { cn } from "../../utils";
import { Tooltip } from "../Tooltip";

export type TabsVariant =
  | "default"
  | "primary"
  | "secondary"
  | "tertiary"
  | "simple"
  | "buy"
  | "sell";

export interface TabItem<T extends string = string> {
  id: T;
  label: React.ReactNode;
  variant?: TabsVariant;
  content?: React.ReactNode;
  tooltip?: React.ReactNode;
  disabled?: boolean;
  className?: string | ((isActive: boolean) => string);
}

export interface TabsProps<T extends string = string> {
  ref?: Ref<HTMLDivElement>;
  value: string;
  items: TabItem<T>[];
  unread?: T[];
  variant?: TabsVariant;
  vertical?: boolean;
  className?: string;
  itemClassName?: string | ((isActive: boolean) => string);
  contentClassName?: string;
  onChange?: (id: T) => void;
}

export const Tabs = <T extends string = string>({
  ref,
  value,
  items,
  unread,
  variant,
  vertical,
  className,
  itemClassName,
  onChange,
  contentClassName,
  ...props
}: TabsProps<T>) => {
  const activeItem = items.find((item) => item.id === value);

  return (
    <>
      <div
        className={cn(
          "ui:inline-flex ui:flex-nowrap ui:text-textPrimary",
          {
            "ui:gap-0 ui:rounded-t": !vertical,
            "ui:flex-col ui:gap-11": vertical,
            "ui:gap-4": vertical && variant === "secondary",
            "ui:gap-0 ui:rounded-sm ui:border ui:border-divider ui:bg-buttonFill":
              variant === "secondary",
            "ui:items-center": variant !== "simple",
            "ui:items-center ui:gap-4": variant === "simple",
          },
          className,
        )}
        {...props}
        ref={ref}
      >
        {items.map((item) => {
          const currentVariant = item.variant ?? variant ?? "default";
          const isActive = item.id === value;
          return (
            <Tooltip
              key={item.id}
              content={item.tooltip}
              disabled={!item.tooltip}
            >
              <button
                disabled={item.disabled}
                className={cn(
                  "ui:flex-1 ui:font-grotesk ui:text-xs ui:transition-colors",
                  {
                    "ui:relative ui:h-8 ui:min-w-25 ui:border-b ui:border-divider ui:before:absolute ui:before:inset-0 ui:before:opacity-0 ui:before:transition-opacity ui:before:ease-in":
                      [
                        "buy",
                        "sell",
                        "default",
                        "primary",
                        "tertiary",
                      ].includes(currentVariant),
                    "ui:border ui:not-first-of-type:border-l-0 ui:first:rounded-l-sm ui:last:rounded-r-sm":
                      currentVariant === "tertiary",
                    "ui:border-b-success ui:text-success":
                      currentVariant === "buy" && isActive,
                    "ui:border-b-error ui:text-error":
                      currentVariant === "sell" && isActive,
                    "ui:border-b-primary ui:dark:border-divider":
                      currentVariant === "default" && isActive,
                    "ui:border-b-primary":
                      ["primary", "tertiary"].includes(currentVariant) &&
                      isActive,

                    "ui:before:bg-[radial-gradient(74.05%_68.75%_at_50.31%_118.75%,_rgba(78,190,150,0.40)_0%,_rgba(78,190,150,0.00)_77%)]":
                      currentVariant === "buy",
                    "ui:before:bg-[radial-gradient(74.05%_68.75%_at_50.31%_118.75%,_rgba(255,86,98,0.40)_0%,_rgba(255,86,98,0.00)_77%)]":
                      currentVariant === "sell",
                    "ui:dark:before:bg-[radial-gradient(45.25%_76.6%_at_50.28%_110.98%,_rgba(235,237,246,0.30)_0%,_rgba(235,237,246,0.00)_100%)]":
                      currentVariant === "default",
                    "ui:dark:before:bg-[linear-gradient(210deg,rgba(57,76,153,0)_60.65%,rgba(56,75,242,0.3)_94.5%)]":
                      currentVariant === "primary" && vertical,
                    "ui:hover:bg-buttonHover":
                      currentVariant === "tertiary" &&
                      !isActive &&
                      !item.disabled,

                    "ui:before:opacity-100": isActive,
                    "ui:text-textSecondary": !isActive,
                    "ui:border-transparent": !isActive && vertical,
                    "ui:hover:text-textPrimary":
                      !isActive &&
                      !item.disabled &&
                      currentVariant !== "tertiary",

                    "ui:pl-0": currentVariant === "simple",
                    "ui:pl-4 ui:text-left": vertical,
                    "ui:min-w-36 ui:py-1": currentVariant === "secondary",
                  },
                  {
                    "ui:cursor-pointer": !item.disabled,
                  },
                  typeof itemClassName === "string"
                    ? itemClassName
                    : itemClassName?.(isActive),
                  typeof item.className === "string"
                    ? item.className
                    : item.className?.(isActive),
                )}
                onClick={() => {
                  if (!item.disabled) {
                    onChange?.(item.id);
                  }
                }}
              >
                {!unread?.length && item.label}
                {unread?.length && (
                  <div className="ui:flex ui:justify-center">
                    <div className="ui:relative">
                      {item.label}
                      <div
                        className={cn(
                          "ui:absolute ui:-top-0.5 ui:-right-1.5 ui:size-1 ui:rounded-full ui:bg-success ui:opacity-0 ui:transition-opacity",
                          {
                            "ui:opacity-100": unread.includes(item.id),
                          },
                        )}
                      />
                    </div>
                  </div>
                )}
              </button>
            </Tooltip>
          );
        })}
      </div>

      {activeItem?.content ? (
        <AnimatePresence mode="wait">
          <motion.div
            key={activeItem.id}
            exit={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            className={contentClassName}
            transition={{
              duration: 0.15,
              ease: "easeInOut",
            }}
          >
            {activeItem.content}
          </motion.div>
        </AnimatePresence>
      ) : null}
    </>
  );
};
